import { Overlay, OverlayContainer } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import { provideHttpClient, withInterceptors, withInterceptorsFromDi } from '@angular/common/http';
import { ErrorHandler, inject, NgModule, provideAppInitializer } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialogModule } from '@angular/material/dialog';
import { MAT_MENU_SCROLL_STRATEGY } from '@angular/material/menu';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { NavigationEnd, NavigationError, Router, RouterModule } from '@angular/router';
import {
  authInterceptor,
  httpErrorInterceptor,
  registerMaterialIconsProviderFabric,
  userErrorInterceptor,
} from '@core';
import { DEFAULT_LOCALE } from '@di-tokens';
import { LogoutComponent } from '@logout';
import * as Sentry from '@sentry/angular';
import { NgxPaginationModule } from 'ngx-pagination';
import { routes } from './routes';
import { AppComponent } from './app.component';
import { CUSTOM_ICONS_MAP } from './config/custom-icons';
import { CustomOverlayContainer } from './config/custom-overlay-container';
import { menuScrollStrategy } from './config/scroll-strategy';
import { AppleEngravingComponent, ApplecareModalComponent } from '@ui';
import SwiperCore from 'swiper';
import { Autoplay, Keyboard, Navigation, Pagination } from 'swiper/modules';
import { filter, take } from 'rxjs';

// Keep the skeleton until the first navigation settles: the auth guard waits for
// the playerId exchange, and the page stays blank until then otherwise.
// NavigationCancel is ignored, since a guard redirect cancels and starts a new navigation.
export function removeLoaderAfterFirstNavigation(): void {
  inject(Router)
    .events.pipe(
      filter((event) => event instanceof NavigationEnd || event instanceof NavigationError),
      take(1),
    )
    .subscribe(() => document.getElementById('skeleton-app-loader')?.remove());
}

SwiperCore.use([Autoplay, Navigation, Keyboard, Pagination]);

@NgModule({
  declarations: [AppComponent],
  bootstrap: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule.forRoot(routes, {
      scrollPositionRestoration: 'top',
      onSameUrlNavigation: 'reload',
    }),
    CommonModule,
    RouterModule,
    NgxPaginationModule,
    LogoutComponent,
    MatDialogModule,
    AppleEngravingComponent,
    ApplecareModalComponent,
  ],
  providers: [
    { provide: OverlayContainer, useClass: CustomOverlayContainer },
    {
      provide: MAT_MENU_SCROLL_STRATEGY,
      useFactory: menuScrollStrategy,
      deps: [Overlay],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
    provideAppInitializer(removeLoaderAfterFirstNavigation),
    {
      provide: DEFAULT_LOCALE,
      useValue: 'en-US',
    },
    provideHttpClient(
      withInterceptors([authInterceptor, userErrorInterceptor, httpErrorInterceptor]),
      withInterceptorsFromDi(),
    ),
    registerMaterialIconsProviderFabric(CUSTOM_ICONS_MAP),
    provideHttpClient(withInterceptorsFromDi()),
  ],
})
export class AppModule {}
